import type { CatalogoRef, DateOnlyString, Paginated } from '../common';
import type { ServicioResponse } from '../servicios';
import type { ClientBalance, InvoiceType } from '../cobros';

export const CATEGORIA_TIPO_SUSCRIPCION = 'TIPO_SUSCRIPCION' as const;

export const TIPO_SUSCRIPCION = {
  MENSUAL: 'MENSUAL',
  PREPAGO: 'PREPAGO',
} as const;

export type TipoSuscripcion = (typeof TIPO_SUSCRIPCION)[keyof typeof TIPO_SUSCRIPCION];

export interface ClienteUbicacion {
  latitud: number;
  longitud: number;
}

export interface ClienteUbicacionPreviewResponse {
  idCliente: number;
  ubicacionCliente: ClienteUbicacion | null;
  ubicacionPago: ClienteUbicacion;
  distanciaMetros: number | null;
  umbralAdvertenciaMetros: number;
  sugerirActualizacion: boolean;
}

export interface ClienteUbicacionUpdateResponse {
  idCliente: number;
  ubicacion: ClienteUbicacion;
  distanciaAnteriorMetros: number | null;
}

export interface ClienteResponse {
  id: number;
  nombre: string | null;
  /** Date-only (fecha_nacimiento DATE column) — see DateOnlyString contract. */
  fechaNacimiento: DateOnlyString | null;
  identificacion: string | null;
  telefonoPrincipal: string | null;
  telefonoReferencia: string | null;
  email: string | null;
  ip: string | null;
  nit: string | null;
  /** Human-readable address/reference retained when GPS is unavailable. */
  direccion: string | null;
  /** Canonical client GPS point; null means it has not been captured yet. */
  ubicacion: ClienteUbicacion | null;
  comentarios: string | null;
  /** Date-only (fecha_cobro DATE column) — see DateOnlyString contract. */
  fechaCobro: DateOnlyString | null;
  diaCobro: number | null;
  /** Real instant (fecha_alta timestamptz column) — NOT date-only. */
  fechaAlta: Date | null;
  fechaCreacion: Date;
  fechaModificacion: Date;
  usuarioModifica: string | null;
  estadoCliente: CatalogoRef | null;
  tipoCliente: CatalogoRef | null;
  /** Subscription type controls the payment modality shown by the frontend. */
  tipoSuscripcion: CatalogoRef | null;
  /**

   * Two independent, non-negative balance values (spec: client-balance-reporting).
   * `deudaPendiente` = sum of open non-annulled invoice remainders.
   * `saldoAFavor` = sum of available credit lots. NEVER netted together.
   */
  deudaPendiente?: string;
  saldoAFavor?: string;
}

/**
 * S4.3 (T27/T29): estado y ventana del paquete prepago que gobierna la
 * cobertura visible del cliente. `estado` es el del paquete (ACTIVO |
 * CANCELADO | CONSUMIDO); solo ACTIVO muestra cobertura. `tieneSnapshot`
 * marca los paquetes adquiridos desde b02e536 (metadata.fechaCobroPrevia):
 * los anteriores conservan el fallback por ventana de pago en el frontend.
 */
export interface ClientePrepagoCobertura {
  idPaquete: number;
  estado: string;
  inicio: DateOnlyString;
  fin: DateOnlyString;
  meses: number | null;
  tieneSnapshot: boolean;
}

/**
 * Additive detail contract: GET /admin/clientes/:id carries `coberturaPrepago`
 * (null when the client has no governing package). Optional so existing
 * `ClienteResponse` consumers and fixtures keep type-checking unchanged.
 */
export type ClienteDetalleResponse = ClienteResponse & {
  coberturaPrepago?: ClientePrepagoCobertura | null;
};

/**
 * Monetary/aggregate summary for the 360 client view.
 * Spec migration: `saldo`/`credito` are read-only compatibility aliases.
 * New code MUST consume `deudaPendiente` and `saldoAFavor` separately.
 */
export interface ClienteResumenResponse {
  /** Deprecated compatibility alias for `deudaPendiente`. */
  saldo: number;
  /** Two-value contract (preferred). Non-negative, never netted. */
  deudaPendiente: string;
  /** Two-value contract (preferred). Non-negative. */
  saldoAFavor: string;
  /**
   * Credit the client can actually apply to future invoices: the sum of
   * AVAILABLE credit lots (cliente_credito.monto_disponible). Distinct from
   * `saldoAFavor`, which is the statement's NET position and also counts
   * funds reserved in prepaid packages and unbilled payments — surfaces that
   * promise applicable credit must read THIS field.
   */
  creditoDisponible: string;
  totalFacturado: number;
  totalPagado: number;
  serviciosActivos: number;
  /**
   * Monthly amount to charge for the active services: sum of positive
   * plan prices across services whose state is ACTIVO or ACTIVO_AL_CORTE
   * (same rule as the services tab's precioTotalActivos). Text NUMERIC;
   * consumers parse with Number().
   */
  serviciosActivosMontoMensual: string;
  /** Deprecated compatibility alias for `saldoAFavor`. */
  credito: number;
}

/**
 * billing-domain-rules (Phase 6.2): per-invoice balance row derived from
 * `view_invoice_balance` for the cliente 360 detail. The sum of every
 * non-annulled `saldoPendiente` MUST equal the parent's `deudaPendiente`.
 * All amounts are text (NUMERIC); consumers parse with `Number()`.
 */
export interface ClienteFacturaBalance {
  id: number;
  idCliente: number;
  tipoFactura: InvoiceType;
  fechaInicio: string | null;
  fechaFin: string | null;
  concepto: string | null;
  gross: string;
  ajustesAplicados: string;
  montoAplicado: string;
  saldoPendiente: string;
  anulada: boolean;
  fechaAnulacion: string | null;
}

/** Stable movement categories exposed by the Cliente 360 subsidiary ledger. */
export const ESTADO_CUENTA_MOVEMENT_TYPE = {
  CARGO: 'CARGO',
  PAGO: 'PAGO',
  PREPAGO: 'PREPAGO',
  CREDITO_GENERADO: 'CREDITO_GENERADO',
  CREDITO_APLICADO: 'CREDITO_APLICADO',
  AJUSTE: 'AJUSTE',
  ANULACION: 'ANULACION',
  REVERSO: 'REVERSO',
  RECONCILIACION: 'RECONCILIACION',
} as const;

export type EstadoCuentaMovementType =
  (typeof ESTADO_CUENTA_MOVEMENT_TYPE)[keyof typeof ESTADO_CUENTA_MOVEMENT_TYPE];

/** Certainty marker for reconstructed historical rows. */
export const ESTADO_CUENTA_CERTAINTY = {
  EXACTA: 'EXACTA',
  LEGACY_INCIERTO: 'LEGACY_INCIERTO',
} as const;

export type EstadoCuentaCertainty =
  (typeof ESTADO_CUENTA_CERTAINTY)[keyof typeof ESTADO_CUENTA_CERTAINTY];

/** Binary formats supported by the complete Cliente 360 statement export. */
export const ESTADO_CUENTA_EXPORT_FORMAT = {
  PDF: 'pdf',
  XLSX: 'xlsx',
} as const;

export type EstadoCuentaExportFormat =
  (typeof ESTADO_CUENTA_EXPORT_FORMAT)[keyof typeof ESTADO_CUENTA_EXPORT_FORMAT];

export const ESTADO_CUENTA_WARNING_CODE = {
  LEGACY_INCIERTO: 'LEGACY_INCIERTO',
  RECONCILIACION_CANONICA: 'RECONCILIACION_CANONICA',
} as const;

export type EstadoCuentaWarningCode =
  (typeof ESTADO_CUENTA_WARNING_CODE)[keyof typeof ESTADO_CUENTA_WARNING_CODE];

/** An application is child detail of a statement movement, never a movement itself. */
export interface EstadoCuentaApplication {
  id: number;
  idFactura: number;
  idPago: number | null;
  idCredito: number | null;
  montoAplicado: string;
  estado: string;
}

/** Optional source detail shown alongside a movement. */
export interface EstadoCuentaMovementDetail {
  tipoFactura?: string | null;
  concepto?: string | null;
  idFactura?: number | null;
  idPago?: number | null;
  idCredito?: number | null;
  idPaquete?: number | null;
  idExcepcion?: number | null;
  periodoInicio?: string | null;
  periodoFin?: string | null;
  motivo?: string | null;
  montoDisponible?: string | null;
  ajustesAplicados?: string | null;
  montoAplicado?: string | null;
  saldoPendiente?: string | null;
}

/** One chronological economic movement with independent balance effects. */
export interface EstadoCuentaMovement {
  id: string;
  fechaEfectiva: string;
  fechaRegistro: string;
  tipo: EstadoCuentaMovementType;
  origen: string;
  monto: string;
  deudaDelta: string;
  saldoAFavorDelta: string;
  deudaAntes: string;
  saldoAFavorAntes: string;
  deudaPendiente: string;
  saldoAFavor: string;
  referencia: string;
  estado: string;
  descripcion: string;
  certeza: EstadoCuentaCertainty;
  auditEventId: number | null;
  detalle?: EstadoCuentaMovementDetail | null;
  aplicaciones?: EstadoCuentaApplication[];
}

export interface EstadoCuentaWarning {
  code: EstadoCuentaWarningCode;
  count: number;
}

/** Paginated Cliente 360 statement response; balances are never netted. */
export interface EstadoCuentaResponse extends Paginated<EstadoCuentaMovement> {
  balanceActual: ClientBalance;
  balanceInicial: ClientBalance;
  advertencias: EstadoCuentaWarning[];
}

/**
 * GET /admin/clientes/:id/servicios — additive per-client service listing.
 * `bajasOcultas` counts the client's services in the BAJA state, independent
 * of pagination and of the list's `estado`/`incluirBajas` filters (0 when
 * the client has no BAJA services). BAJA_AL_CORTE is not counted — it keeps
 * billing until the cutoff. The field is always present in the response.
 */
export interface ClienteServiciosResponse extends Paginated<ServicioResponse> {
  bajasOcultas: number;
}

/** Configuration shared by the monthly financial-history projection. */
export const ESTADO_CUENTA_SERIE_MENSUAL = {
  DEFAULT_MONTHS: 12,
  MAX_MONTHS: 24,
  TIMEZONE: 'America/Guatemala',
} as const;

/** One exact calendar month in the Cliente 360 financial-history response. */
export interface EstadoCuentaSerieMensualBucket {
  /** YYYY-MM bucket key. */
  mes: string;
  fechaDesde: string;
  fechaHasta: string;
  facturado: string;
  pagado: string;
  deudaPendiente: string;
  saldoAFavor: string;
}

/** Period metadata for a monthly financial-history response. */
export interface EstadoCuentaSerieMensualPeriod {
  fechaDesde: string;
  fechaHasta: string;
  meses: number;
  zonaHoraria: string;
}

/** Explicit metric semantics carried by the monthly response. */
export const ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS = {
  FACTURADO: 'EMISION_ACTIVA',
  PAGADO: 'EFECTIVO_RECIBIDO',
} as const;

export interface EstadoCuentaSerieMensualSemantics {
  /** Gross active invoice amount bucketed by cobro_cliente.fecha_creacion. */
  facturado: typeof ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS.FACTURADO;
  /** Positive VIGENTE payment amount bucketed by pago_cliente.fecha_pago. */
  pagado: typeof ESTADO_CUENTA_SERIE_MENSUAL_SEMANTICS.PAGADO;
}

/** Non-paginated monthly financial-history response for Cliente 360. */
export interface EstadoCuentaSerieMensualResponse {
  data: EstadoCuentaSerieMensualBucket[];
  periodo: EstadoCuentaSerieMensualPeriod;
  semantica: EstadoCuentaSerieMensualSemantics;
  balanceInicial: ClientBalance;
  balanceFinal: ClientBalance;
  advertencias: EstadoCuentaWarning[];
}

// ---------------------------------------------------------------------------
// Simple statement (bank-style): 3 movement types + period summary + running
// balance. Replaces the audit subledger for the daily "estado de cuenta" view.
// Canonical net = pagos VIGENTE + excepciones - cobros vivos (== view_saldo).
// ---------------------------------------------------------------------------

/** The only three economic movement types the simple statement surfaces. */
export const ESTADO_CUENTA_SIMPLE_TIPO = {
  FACTURA: 'FACTURA',
  PAGO: 'PAGO',
  EXCEPCION: 'EXCEPCION',
} as const;

export type EstadoCuentaSimpleTipo =
  (typeof ESTADO_CUENTA_SIMPLE_TIPO)[keyof typeof ESTADO_CUENTA_SIMPLE_TIPO];

/** One simple statement row: a single economic event with its running net balance. */
export interface EstadoCuentaSimpleMovement {
  id: string;
  tipo: EstadoCuentaSimpleTipo;
  /** Effective (business/period) date, ISO (YYYY-MM-DD). */
  fecha: string;
  /** Registration/creation date of the record, ISO (YYYY-MM-DD). The statement is
   * ordered by this (the real application timeline); `fecha` is the effective
   * business date, kept visible so the period is not lost. */
  fechaRegistro?: string | null;
  referencia: string;
  concepto: string;
  /** Per-type state: invoice PAGADA/VIGENTE/ANULADA, payment VIGENTE/ANULADO,
   * exception RECARGO/DESCUENTO. */
  estado: string;
  /** Amount that INCREASES debt (invoice gross, or recargo). */
  debito: string;
  /** Amount that DECREASES debt (VIGENTE payment, or descuento). */
  credito: string;
  /** Billing period start/end (ISO). Set for invoices, null otherwise. */
  periodoInicio?: string | null;
  periodoFin?: string | null;
  /** Running NET balance after this row, in creation-date (application-
   * timeline) order: >0 = credit (a favor), <0 = debt. The final value reconciles
   * to view_saldo_cliente; the total is order-independent. */
  saldo: string;
  /** Active invoice applications a PAGO has already settled (invoice id + amount);
   * set on PAGO rows only, undefined otherwise. */
  aplicadoA?: Array<{ idFactura: number; monto: string }>;
  /** Invoice-side applications that settled this FACTURA: one entry per
   * pago_aplicacion row (payment- or credit-funded) with its state, so the
   * invoice row answers which payment(s) paid it without a second query.
   * Set on FACTURA rows only; undefined when the invoice has no applications. */
  aplicaciones?: EstadoCuentaApplication[];
  /** Funded coverage that already settled this FACTURA: payments directly, or the
   * reference of the credit lot that paid it — an exception (AJUSTE_sobrepago) or
   * the funding payment (PAGO_sobrepago). `idPago` stays set for payment links;
   * `source` says what `idReferencia` is (`idCredito` carries the lot id), so an
   * exception-funded link is never shown as a payment. Set on FACTURA rows only. */
  pagadoPor?: Array<{
    idPago?: number;
    source?: 'PAGO' | 'EXCEPCION' | 'CREDITO';
    idReferencia?: number;
    idCredito?: number;
    monto: string;
  }>;
  /** Parked credit attached to this PAGO: available amount of cliente_credito
   * lots that reference the payment (id_referencia = payment id,
   * monto_disponible > 0) and have no application rows yet. '0.00' when the
   * payment carries no parked credit; null on non-PAGO rows. */
  creditPark?: string | null;
}

/** Period summary for the simple statement. */
export interface EstadoCuentaSimpleResumen {
  /** Selected period bounds; both null when the full history is requested. */
  desde: string | null;
  hasta: string | null;
  /** Net balance just before `desde` (0 when the full history is requested). */
  saldoInicial: string;
  /** Gross active invoice amount within the period (debt). */
  facturado: string;
  /** VIGENTE payment amount within the period (credit). */
  pagado: string;
  /** Signed exception amount within the period (descuento +, recargo −). */
  excepciones: string;
  /** Net balance as of `hasta` (or the latest movement when the full history). */
  saldoFinal: string;
}

/** Paginated simple statement response. */
export interface EstadoCuentaSimpleResponse {
  clienteId: number;
  resumen: EstadoCuentaSimpleResumen;
  data: EstadoCuentaSimpleMovement[];
  total: number;
  limit: number;
  offset: number;
}

// ---------------------------------------------------------------------------
// Client lifecycle events (public.cliente_evento): ALTA / BAJA /
// REACTIVACION rows emitted by the billing domain and read by the
// client 360 view and the dashboard movement projection.
// ---------------------------------------------------------------------------

/** Client lifecycle event types (public.cliente_evento.tipo_evento). */
export const CLIENTE_EVENTO_TIPO = {
  ALTA: 'ALTA',
  BAJA: 'BAJA',
  REACTIVACION: 'REACTIVACION',
} as const;

export type ClienteEventoTipo = (typeof CLIENTE_EVENTO_TIPO)[keyof typeof CLIENTE_EVENTO_TIPO];

/** One client lifecycle event row from public.cliente_evento. */
export interface ClienteEvento {
  id: number;
  idCliente: number;
  tipoEvento: ClienteEventoTipo;
  /** Date-only (fecha_evento DATE column). */
  fechaEvento: DateOnlyString;
  idOperacion: number | null;
  actor: string;
  /** JSONB sidecar; known key today: `fuente` (e.g. 'backfill_alta', 'desactivacion_flujo'). */
  metadata: Record<string, unknown>;
  /** Row creation instant (timestamptz column). */
  fechaCreacion: Date;
}
